// First: the i18n layer initialises synchronously, before anything renders.
import './i18n';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, StrictMode, Suspense, type ComponentType, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell, BuilderSuspensionGate } from './app/AppShell';
import { ErrorBoundary } from './app/ErrorBoundary';
import { SessionProvider, useSession } from './app/session';
import { Spinner } from './components/ui';
import { ToastProvider } from './components/ui/toast';
import {
  AcceptInvitePage,
  ForgotPasswordPage,
  LoginPage,
  RegisterPage,
  ResetPasswordPage,
  VerifyEmailPage,
} from './features/auth/pages';
import { PlanBlockToaster } from './features/billing/plan';
import { DashboardPage } from './features/dashboard/DashboardPage';
import { CreateWorkspacePage } from './features/workspaces/pages';
import './styles/tokens.css';

/** `lazy()` for a named export, so page modules keep their named exports. */
function lazyPage<K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) {
  return lazy(() => load().then((m) => ({ default: m[name] })));
}

// Route-level code splitting: a page's code loads the first time someone opens
// it (the shell shows a spinner meanwhile). The auth pages and the dashboard
// stay in the main chunk — they are the first paint — and React Flow only
// loads with the builder.
const AutomationBuilderPage = lazyPage(() => import('./features/automations/BuilderPage'), 'AutomationBuilderPage');
const ContactsListPage = lazyPage(() => import('./features/contacts/ListPage'), 'ContactsListPage');
const ContactDetailPage = lazyPage(() => import('./features/contacts/DetailPage'), 'ContactDetailPage');
const AutomationsListPage = lazyPage(() => import('./features/automations/ListPage'), 'AutomationsListPage');
const QuickAutomationPage = lazyPage(() => import('./features/automations/quick/QuickAutomationPage'), 'QuickAutomationPage');
const InboxPage = lazyPage(() => import('./features/inbox/InboxPage'), 'InboxPage');
const TemplatesPage = lazyPage(() => import('./features/templates/TemplatesPage'), 'TemplatesPage');
const PostsPage = lazyPage(() => import('./features/posts/PostsPage'), 'PostsPage');
const StoriesPage = lazyPage(() => import('./features/posts/StoriesPage'), 'StoriesPage');
const ComposerPage = lazyPage(() => import('./features/posts/ComposerPage'), 'ComposerPage');
const CampaignsPage = lazyPage(() => import('./features/campaigns/CampaignsPage'), 'CampaignsPage');
const CampaignDetailPage = lazyPage(() => import('./features/campaigns/CampaignDetailPage'), 'CampaignDetailPage');
const PeoplePage = lazyPage(() => import('./features/settings/PeoplePage'), 'PeoplePage');
const TagsPage = lazyPage(() => import('./features/settings/TagsPage'), 'TagsPage');
const InboxSettingsPage = lazyPage(() => import('./features/settings/InboxSettingsPage'), 'InboxSettingsPage');
const ChannelsPage = lazyPage(() => import('./features/settings/ChannelsPage'), 'ChannelsPage');
const ChannelMenuPage = lazyPage(() => import('./features/settings/ChannelMenuPage'), 'ChannelMenuPage');
const FieldsPage = lazyPage(() => import('./features/settings/FieldsPage'), 'FieldsPage');
const UsagePage = lazyPage(() => import('./features/settings/UsagePage'), 'UsagePage');
const DevelopersPage = lazyPage(() => import('./features/settings/DevelopersPage'), 'DevelopersPage');
const SettingsLayout = lazyPage(() => import('./features/settings/SettingsLayout'), 'SettingsLayout');
const AnalyticsOverviewPage = lazyPage(() => import('./features/analytics/OverviewPage'), 'AnalyticsOverviewPage');
// The component catalogue (redesign): development builds only. In a production
// build the condition is false at compile time, so the page is never bundled.
const UiCatalog = import.meta.env.DEV ? lazyPage(() => import('./dev/UiCatalog'), 'UiCatalog') : null;
const AutomationAnalyticsPage = lazyPage(() => import('./features/analytics/AutomationAnalyticsPage'), 'AutomationAnalyticsPage');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 15_000 },
  },
});

function FullPageSpinner() {
  return (
    <div className="flex min-h-dvh items-center justify-center">
      <Spinner label="Loading Repliq" />
    </div>
  );
}

/**
 * Gate for the authenticated area. Three states, in order: still resolving the
 * session, signed out, and signed in but without a workspace yet — the last is
 * a real state because registration deliberately does not create one.
 */
function RequireWorkspace({ children }: { children: ReactNode }) {
  const { session, isLoading } = useSession();
  const location = useLocation();

  if (isLoading) return <FullPageSpinner />;
  if (!session) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (!session.activeWorkspaceId) return <Navigate to="/onboarding/workspace" replace />;
  return <>{children}</>;
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { session, isLoading } = useSession();
  if (isLoading) return <FullPageSpinner />;
  if (!session) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      {UiCatalog ? (
        <Route
          path="/__ui"
          element={
            <Suspense fallback={<FullPageSpinner />}>
              <UiCatalog />
            </Suspense>
          }
        />
      ) : null}

      <Route
        path="/onboarding/workspace"
        element={
          <RequireAuth>
            <CreateWorkspacePage />
          </RequireAuth>
        }
      />

      {/* The builder is a full-screen editor: gated like the app, but outside the shell chrome. */}
      <Route
        path="/automations/:automationId"
        element={
          <RequireWorkspace>
            {/* Suspended, it shows the suspended screen inside the shell (D-183). */}
            <BuilderSuspensionGate>
              <Suspense fallback={<FullPageSpinner />}>
                <AutomationBuilderPage />
              </Suspense>
            </BuilderSuspensionGate>
          </RequireWorkspace>
        }
      />

      <Route
        element={
          <RequireWorkspace>
            <AppShell />
          </RequireWorkspace>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="/inbox" element={<InboxPage />} />
        <Route path="/inbox/:conversationId" element={<InboxPage />} />
        <Route path="/automations" element={<AutomationsListPage />} />
        {/* Static segment, so it outranks the builder's `/automations/:automationId`. */}
        <Route path="/automations/quick" element={<QuickAutomationPage />} />
        <Route path="/templates" element={<TemplatesPage />} />
        <Route path="/posts" element={<PostsPage />} />
        <Route path="/posts/new" element={<ComposerPage />} />
        <Route path="/posts/:postId/edit" element={<ComposerPage />} />
        {/* Stories are a section of their own (D-137); the composer reads its kind from the path. */}
        <Route path="/stories" element={<StoriesPage />} />
        <Route path="/stories/new" element={<ComposerPage />} />
        <Route path="/stories/:postId/edit" element={<ComposerPage />} />
        <Route path="/campaigns" element={<CampaignsPage />} />
        <Route path="/campaigns/:campaignId" element={<CampaignDetailPage />} />
        <Route path="/analytics" element={<AnalyticsOverviewPage />} />
        <Route path="/analytics/automations/:automationId" element={<AutomationAnalyticsPage />} />
        <Route path="/contacts" element={<ContactsListPage />} />
        <Route path="/contacts/:contactId" element={<ContactDetailPage />} />
        {/* One Settings screen with a sub-nav (refonte); Channels keeps its own place. */}
        <Route path="/settings" element={<SettingsLayout />}>
          <Route index element={<Navigate to="people" replace />} />
          <Route path="people" element={<PeoplePage />} />
          <Route path="inbox" element={<InboxSettingsPage />} />
          <Route path="tags" element={<TagsPage />} />
          <Route path="fields" element={<FieldsPage />} />
          <Route path="usage" element={<UsagePage />} />
          <Route path="developers" element={<DevelopersPage />} />
        </Route>
        <Route path="/settings/channels" element={<ChannelsPage />} />
        <Route path="/settings/channels/:accountId/menu" element={<ChannelMenuPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ErrorBoundary>
          <SessionProvider>
            <ToastProvider>
              {/* Every refused write (402) becomes one toast with "Changer d'offre" (D-183). */}
              <PlanBlockToaster />
              <App />
            </ToastProvider>
          </SessionProvider>
        </ErrorBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
